import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '../../components/ui/Card';
import { Package, RefreshCw, Database, TrendingDown, DollarSign, ShoppingBag, Layers, ChevronDown, ChevronRight } from 'lucide-react';

const formatNumber = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('en-US');
};
const formatShortNumber = (value) => {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs < 10_000_000 ? 1 : 0)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(abs < 10_000 ? 1 : 0)}K`;
  return n.toString();
};
const fmtCurrency = (n) => {
  const v = Number(n || 0);
  if (!v) return '-';
  return '₺' + v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};
const API_BASE = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';

export default function IkasStockByBrand() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [sortKey, setSortKey] = useState('stockCost');
  const [selectedDate, setSelectedDate] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [source, setSource] = useState('db');
  const [sectionTab, setSectionTab] = useState('brand');
  const [searchTerm, setSearchTerm] = useState('');

  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const headers = { 'X-Brand-ID': brandId || '' };
  const fetchOpts = { credentials: 'include', headers };

  useEffect(() => {
    if (!brandId) return;
    fetch(`${API_BASE}/api/ikas/stock/dates?brandId=${brandId}`, fetchOpts)
      .then(r => r.json())
      .then(json => {
        if (json.ok && json.dates?.length) {
          setAvailableDates(json.dates);
          setSelectedDate(json.dates[0].date);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  const fetchFromDB = async (date) => {
    setLoading(true);
    setError(null);
    try {
      if (!brandId) throw new Error('No brand selected');
      const d = date || selectedDate;
      const res = await fetch(`${API_BASE}/api/ikas/stock/snapshot?brandId=${brandId}&date=${d}`, fetchOpts);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Error');
      setData(json);
      setSource('db');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchLive = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!brandId) throw new Error('No brand selected');
      const res = await fetch(`${API_BASE}/api/ikas/stock/by-brand?brandId=${brandId}`, fetchOpts);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Error');
      setData(json);
      setSource('live');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDate) fetchFromDB(selectedDate);
    // eslint-disable-next-line
  }, [selectedDate]);

  const toggle = (name) => setExpanded(prev => ({ ...prev, [name]: !prev[name] }));

  const totals = useMemo(() => {
    if (!data?.brands) return { stock: 0, cost: 0, retail: 0, products: 0, brands: 0, margin: 0 };
    const stock = data.brands.reduce((s, b) => s + (b.totalStock || 0), 0);
    const cost = data.totalStockCost || 0;
    const retail = data.totalRetailValue || 0;
    const margin = cost > 0 ? ((retail - cost) / retail * 100) : 0;
    return { stock, cost, retail, products: data.totalProducts || 0, brands: data.totalBrands || 0, margin };
  }, [data]);

  const sortedBrands = useMemo(() => {
    if (!data?.brands) return [];
    return [...data.brands].sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));
  }, [data, sortKey]);

  // All products flat for product tab
  const allProducts = useMemo(() => {
    if (!data?.brands) return [];
    const arr = [];
    for (const b of data.brands) {
      for (const p of (b.products || [])) {
        arr.push({ ...p, brandName: b.brandName });
      }
    }
    return arr.sort((a, b) => b.stockCost - a.stockCost);
  }, [data]);

  const filteredProducts = useMemo(() => {
    if (!searchTerm.trim()) return allProducts.slice(0, 100);
    const q = searchTerm.toLowerCase();
    return allProducts.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.brandName || '').toLowerCase().includes(q) ||
      (p.categories || []).some(c => c.toLowerCase().includes(q))
    ).slice(0, 100);
  }, [allProducts, searchTerm]);

  const sortBtns = [
    { key: 'stockCost', label: 'Stock Cost' },
    { key: 'totalStock', label: 'Stock Qty' },
    { key: 'retailValue', label: 'Retail Value' },
    { key: 'productCount', label: 'Products' },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-3">
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <img src="/ikaslogoicon.png" alt="Ikas" className="w-10 h-10 object-contain rounded-lg" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Stock & Cost Analysis</h1>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {availableDates.length > 0 && (
              <select
                className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300 cursor-pointer"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              >
                {availableDates.map(d => (
                  <option key={d.date} value={d.date}>
                    {new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </option>
                ))}
              </select>
            )}
            <button
              className="px-3 py-1.5 text-sm font-medium rounded-lg bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200 transition-colors flex items-center gap-1.5 disabled:opacity-60"
              onClick={fetchLive}
              disabled={loading}
            >
              <RefreshCw className="w-4 h-4" />
              Live
            </button>
          </div>
        </div>
      </div>

      {/* Source badge */}
      {data && (
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
            source === 'db' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'
          }`}>
            {source === 'db' ? <Database className="w-3 h-3" /> : <RefreshCw className="w-3 h-3" />}
            {source === 'db' ? `Saved — ${data.date || selectedDate}` : 'Live Ikas API'}
          </span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <span className="text-red-600 font-semibold text-sm">{error}</span>
        </div>
      )}

      {loading && !data && (
        <div className="text-center py-16">
          <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin mx-auto" />
          <p className="mt-3 text-gray-500 text-sm">Loading data...</p>
        </div>
      )}

      {data && (
        <>
          {/* KPI Cards */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-gray-500">Stock Cost</p>
                    <p className="text-lg font-bold mt-1 text-red-600">₺{formatShortNumber(totals.cost)}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-red-50">
                    <DollarSign className="w-4 h-4 text-red-500" />
                  </div>
                </div>
              </Card>
              <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-gray-500">Retail Value</p>
                    <p className="text-lg font-bold mt-1 text-emerald-600">₺{formatShortNumber(totals.retail)}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-emerald-50">
                    <TrendingDown className="w-4 h-4 text-emerald-500" />
                  </div>
                </div>
              </Card>
              <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-gray-500">Gross Margin</p>
                    <p className="text-lg font-bold mt-1 text-violet-600">{totals.margin.toFixed(1)}%</p>
                  </div>
                  <div className="p-2 rounded-lg bg-violet-50">
                    <Layers className="w-4 h-4 text-violet-500" />
                  </div>
                </div>
              </Card>
              <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-gray-500">Total Stock</p>
                    <p className="text-lg font-bold mt-1">{formatShortNumber(totals.stock)}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-indigo-50">
                    <Package className="w-4 h-4 text-indigo-500" />
                  </div>
                </div>
              </Card>
              <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-gray-500">Products</p>
                    <p className="text-lg font-bold mt-1">{formatNumber(totals.products)}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-sky-50">
                    <ShoppingBag className="w-4 h-4 text-sky-500" />
                  </div>
                </div>
              </Card>
              <Card className="p-4 rounded-xl shadow-xl border bg-white border-gray-100">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-gray-500">Brands</p>
                    <p className="text-lg font-bold mt-1">{formatNumber(totals.brands)}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-amber-50">
                    <Layers className="w-4 h-4 text-amber-500" />
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Section Tabs */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-3">
            <div className="flex items-center gap-2">
              {[
                { id: 'brand', label: 'By Brand' },
                { id: 'product', label: 'By Product' },
              ].map(opt => (
                <button
                  key={opt.id}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    sectionTab === opt.id
                      ? 'bg-gray-900 text-white shadow-sm'
                      : 'text-gray-700 bg-gray-50 hover:bg-white hover:text-gray-900 border border-gray-200'
                  }`}
                  onClick={() => setSectionTab(opt.id)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Brand Tab */}
          {sectionTab === 'brand' && (
            <div className="bg-white border rounded-2xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="text-lg font-semibold">Stock by Brand</div>
                <div className="flex gap-1.5">
                  {sortBtns.map(c => (
                    <button
                      key={c.key}
                      onClick={() => setSortKey(c.key)}
                      className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                        sortKey === c.key ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >{c.label}</button>
                  ))}
                </div>
              </div>
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="py-2 pr-2 w-8"></th>
                      <th className="py-2 pr-4">Brand</th>
                      <th className="py-2 pr-4 text-right">Stock Cost</th>
                      <th className="py-2 pr-4 text-right">Retail Value</th>
                      <th className="py-2 pr-4 text-right">Stock</th>
                      <th className="py-2 pr-4 text-right">Products</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedBrands.map((b) => {
                      const isOpen = expanded[b.brandName];
                      return (
                        <React.Fragment key={b.brandName}>
                          <tr
                            className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => toggle(b.brandName)}
                          >
                            <td className="py-2 pr-2">
                              {isOpen
                                ? <ChevronDown className="w-4 h-4 text-indigo-500" />
                                : <ChevronRight className="w-4 h-4 text-gray-400" />}
                            </td>
                            <td className="py-2 pr-4 font-semibold">{b.brandName}</td>
                            <td className="py-2 pr-4 text-right font-bold text-red-600">{fmtCurrency(b.stockCost)}</td>
                            <td className="py-2 pr-4 text-right font-semibold text-emerald-600">{fmtCurrency(b.retailValue)}</td>
                            <td className="py-2 pr-4 text-right font-bold text-indigo-600">{formatNumber(b.totalStock)}</td>
                            <td className="py-2 pr-4 text-right">{formatNumber(b.productCount)}</td>
                          </tr>
                          {isOpen && [...b.products].sort((a, c) => c.stockCost - a.stockCost).slice(0, 50).map(p => (
                            <tr key={p.id} className="bg-indigo-50/40 border-b border-indigo-100">
                              <td></td>
                              <td className="py-1.5 pr-4 pl-6 text-xs text-gray-600">
                                {p.name}
                                {p.categories?.length > 0 && (
                                  <span className="ml-1.5 text-gray-400">({p.categories.join(', ')})</span>
                                )}
                              </td>
                              <td className="py-1.5 pr-4 text-right text-xs">
                                <span className="font-semibold text-red-500">{fmtCurrency(p.stockCost)}</span>
                                {p.buyPrice > 0 && <span className="block text-gray-400 text-[10px]">unit: {fmtCurrency(p.buyPrice)}</span>}
                              </td>
                              <td className="py-1.5 pr-4 text-right text-xs">
                                <span className="font-semibold text-emerald-500">{fmtCurrency(p.retailValue)}</span>
                                {p.sellPrice > 0 && <span className="block text-gray-400 text-[10px]">
                                  {p.discountPrice > 0
                                    ? <><s>{fmtCurrency(p.sellPrice)}</s> → {fmtCurrency(p.discountPrice)}</>
                                    : `unit: ${fmtCurrency(p.sellPrice)}`}
                                </span>}
                              </td>
                              <td className="py-1.5 pr-4 text-right text-xs font-semibold">{formatNumber(p.totalStock)}</td>
                              <td></td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Product Tab */}
          {sectionTab === 'product' && (
            <div className="bg-white border rounded-2xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="text-lg font-semibold">Stock by Product</div>
                <input
                  type="text"
                  placeholder="Search product, brand or category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 w-72 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
              </div>
              <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="py-2 pr-4">Product</th>
                      <th className="py-2 pr-4">Brand</th>
                      <th className="py-2 pr-4 text-right">Buy Price</th>
                      <th className="py-2 pr-4 text-right">Sell Price</th>
                      <th className="py-2 pr-4 text-right">Discounted</th>
                      <th className="py-2 pr-4 text-right">Stock Cost</th>
                      <th className="py-2 pr-4 text-right">Retail Value</th>
                      <th className="py-2 pr-4 text-right">Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map((p, i) => (
                      <tr key={`${p.id}-${i}`} className="border-b hover:bg-gray-50">
                        <td className="py-2 pr-4 max-w-xs truncate">
                          <span className="font-medium">{p.name}</span>
                          {p.categories?.length > 0 && (
                            <span className="ml-1.5 text-xs text-gray-400">({p.categories.join(', ')})</span>
                          )}
                        </td>
                        <td className="py-2 pr-4 text-gray-600">{p.brandName}</td>
                        <td className="py-2 pr-4 text-right font-medium text-amber-600">{p.buyPrice > 0 ? fmtCurrency(p.buyPrice) : '-'}</td>
                        <td className="py-2 pr-4 text-right text-gray-600">{p.sellPrice > 0 ? fmtCurrency(p.sellPrice) : '-'}</td>
                        <td className="py-2 pr-4 text-right text-violet-600 font-medium">{p.discountPrice > 0 ? fmtCurrency(p.discountPrice) : '-'}</td>
                        <td className="py-2 pr-4 text-right font-bold text-red-600">{fmtCurrency(p.stockCost)}</td>
                        <td className="py-2 pr-4 text-right font-semibold text-emerald-600">{fmtCurrency(p.retailValue)}</td>
                        <td className="py-2 pr-4 text-right font-bold">{formatNumber(p.totalStock)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredProducts.length >= 100 && (
                  <div className="text-center py-3 text-xs text-gray-400">Showing first 100 products. Use search to narrow down.</div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
